import {
  ClientResponse,
  CreateClientData,
  CreateClientParams,
  CreateContactClient,
  IContactClient,
  ISearchClientParams
} from './customer.model';
import { BaseQueryParams } from '../../Global.model';
import { twApi } from '../api';

/**
 * Api endpoints for customers
 */

const customerApi = twApi.injectEndpoints({
  endpoints: (builder) => ({
    getCustomers: builder.query<{ content: ClientResponse[]; total: number }, BaseQueryParams>({
      query: (arg) => ({
        url: `v2/customers`,
        params: { ...arg }
      }),
      transformResponse: (response: { data: ClientResponse[]; total: number }) => ({
        content: response.data,
        total: response.total
      }),
      providesTags: (result = { content: [], total: 0 }) =>
        result?.content
          ? [
              ...result.content.map(({ id }) => ({ type: 'Customers' as const, id })),
              { type: 'Customers', id: 'CUSTOMERS_LIST' }
            ]
          : [{ type: 'Customers', id: 'CUSTOMERS_LIST' }]
    }),
    searchCustomers: builder.query<
      { content: ClientResponse[]; total: number },
      { pageParams?: BaseQueryParams; filterParams?: ISearchClientParams }
    >({
      query: (customerSearchParams) => {
        const { pageParams, filterParams } = customerSearchParams;

        return {
          url: `v2/customers/search`,
          method: 'POST',
          body: { ...filterParams },
          params: { ...pageParams }
        };
      },
      transformResponse: (response: { data: ClientResponse[]; total: number }) => ({
        content: response.data,
        total: response.total
      }),
      providesTags: (result = { content: [], total: 0 }) =>
        result?.content
          ? [
              ...result.content.map(({ id }) => ({ type: 'Customers' as const, id })),
              { type: 'Customers', id: 'CUSTOMERS_LIST' }
            ]
          : [{ type: 'Customers', id: 'CUSTOMERS_LIST' }]
    }),
    getCustomerById: builder.query<ClientResponse, string>({
      query: (id) => ({
        url: `v2/customers/${id}`
      }),
      transformResponse: (response: { data: ClientResponse }) => {
        return response.data;
      },
      providesTags: (result, error, id) => [{ type: 'Customers' as const, id }]
    }),
    createCustomer: builder.mutation<ClientResponse, CreateClientData>({
      query: (customer) => {
        const {
          companyName,
          companyEmail,
          companyPhone,
          type,
          phoneExt,
          locationId,
          externalCustomerId,
          currencyPreference
        } = customer;

        const params: CreateClientParams = {
          name: companyName
        };

        !!type ? (params.type = type) : null;
        !!companyEmail ? (params.billing_email = companyEmail) : null;
        !!companyPhone ? (params.primary_phone = companyPhone.toString()) : null;
        !!phoneExt ? (params.phone_ext = phoneExt) : null;
        !!locationId ? (params.location_id = locationId) : null;
        !!externalCustomerId ? (params.external_customer_id = externalCustomerId) : null;
        !!currencyPreference ? (params.currency_preference = currencyPreference) : null;
        return {
          url: 'v2/customers',
          method: 'POST',
          body: { ...params }
        };
      },
      transformResponse: (response: { data: ClientResponse; message: string }) => response.data,
      invalidatesTags: [{ type: 'Customers', id: 'CUSTOMERS_LIST' }]
    }),
    updateCustomer: builder.mutation<ClientResponse, CreateClientData>({
      query: (clientData: CreateClientData) => {
        const {
          companyId,
          companyName,
          billingEmail,
          primaryPhone,
          locationId,
          phoneExt,
          externalCustomerId,
          note,
          preferences,
          currencyPreference
        } = clientData;

        const params: CreateClientParams = {
          name: companyName,
          billing_email: !!billingEmail ? billingEmail : null,
          primary_phone: !!primaryPhone ? primaryPhone.toString() : null,
          phone_ext: !!phoneExt ? phoneExt : undefined,
          external_customer_id: !!externalCustomerId ? externalCustomerId : null,
          currency_preference: !!currencyPreference ? currencyPreference : null
        };
        if (locationId) {
          params.location_id = locationId;
        }
        if (note) {
          params.note = note;
        }
        if (preferences) {
          params.preferences = preferences;
        }

        return {
          url: `v2/customers/${companyId}`,
          method: 'PUT',
          body: { ...params }
        };
      },
      transformResponse: (response: { data: ClientResponse; message: string }) => response.data,
      invalidatesTags: (result, error, data) => [
        { type: 'Customers', id: data.companyId },
        { type: 'Customers', id: 'CUSTOMERS_LIST' }
      ]
    }),
    deleteCustomer: builder.mutation({
      query: (id) => ({
        url: `v2/customers/${id}`,
        method: 'DELETE'
      }),
      onQueryStarted: (id, { dispatch, queryFulfilled }) => {
        const update = dispatch(
          customerApi.util.updateQueryData('getCustomers', {}, (customerData) => {
            const index = customerData.content.findIndex((c) => c.id === id);
            if (index !== -1) {
              // Make sure the item exists
              customerData.content.splice(index, 1); // Remove 1 item at index
              customerData.total -= 1;
            }
          })
        );
        queryFulfilled.catch(() => {
          update.undo();
        });
      },
      invalidatesTags: (result, error, id) => [
        { type: 'Customers', id },
        { type: 'Customers', id: 'CUSTOMERS_LIST' }
      ]
    }),
    getContacts: builder.query<
      { content: IContactClient[]; total: number },
      BaseQueryParams & { customerId: string }
    >({
      query: ({ customerId, ...args }) => ({
        url: `v2/customers/${customerId}/contacts`,
        params: args
      }),
      transformResponse: (response: { data: IContactClient[]; total: number }) => ({
        content: response.data,
        total: response.total
      }),
      providesTags: (result = { content: [], total: 0 }) =>
        result.content
          ? [
              ...result.content.map(({ id }) => ({ type: 'Customers-Contacts' as const, id })),
              { type: 'Customers-Contacts', id: 'CUSTOMER_CONTACTS_LIST' }
            ]
          : [{ type: 'Customers-Contacts', id: 'CUSTOMER_CONTACTS_LIST' }]
    }),
    getContactById: builder.query<IContactClient, { customerId: string; id: string }>({
      query: ({ customerId, id }) => ({
        url: `v2/customers/${customerId}/contacts/${id}`
      }),
      transformResponse: (response: { data: IContactClient }) => {
        return response.data;
      },
      providesTags: (result, error, { id }) => [{ type: 'Customers-Contacts' as const, id }]
    }),
    createContact: builder.mutation<IContactClient, CreateContactClient>({
      query: (contact) => {
        const {
          email_address,
          phone,
          first_name,
          last_name,
          phone_ext,
          create_user_and_send_invite
        } = contact;
        const body: CreateContactClient = {
          first_name: first_name,
          last_name: last_name,
          create_user_and_send_invite: create_user_and_send_invite
        };

        if (email_address) {
          body.email_address = email_address;
        }
        if (phone) {
          body.phone = phone;
        }
        if (phone_ext) {
          body.phone_ext = phone_ext;
        }

        return {
          url: `v2/customers/${contact.client_id}/contacts`,
          method: 'POST',
          body: { ...body }
        };
      },
      transformResponse: (response: { data: IContactClient; message: string }) => response.data,
      invalidatesTags: (result, error, data) => [
        { type: 'Customers-Contacts', id: 'CUSTOMER_CONTACTS_LIST' },
        { type: 'Customers', id: data.client_id }
      ]
    }),
    updateContact: builder.mutation<IContactClient, IContactClient>({
      query: (contact) => {
        const { email_address, phone, first_name, last_name, phone_ext } = contact;
        const body: IContactClient = {
          first_name: first_name,
          last_name: last_name
        };

        if (email_address) {
          body.email_address = email_address;
        }
        if (phone) {
          body.phone = phone;
        }
        if (phone_ext) {
          body.phone_ext = phone_ext;
        }

        return {
          url: `v2/customers/${contact.customer_id}/contacts/${contact.id}`,
          method: 'PUT',
          body: { ...body }
        };
      },
      transformResponse: (response: { data: IContactClient; message: string }) => response.data,
      invalidatesTags: (result, error, { id, customer_id }) => [
        { type: 'Customers-Contacts', id },
        { type: 'Customers-Contacts', id: 'CUSTOMER_CONTACTS_LIST' },
        { type: 'Customers', id: customer_id }
      ]
    }),
    deleteContact: builder.mutation<IContactClient, { customerId: string; id: string }>({
      query: ({ customerId, id }) => ({
        url: `v2/customers/${customerId}/contacts/${id}`,
        method: 'DELETE'
      }),
      onQueryStarted: ({ id, customerId }, { dispatch, queryFulfilled }) => {
        const update = dispatch(
          customerApi.util.updateQueryData('getContacts', { customerId }, (contactData) => {
            const index = contactData.content.findIndex((c) => c.id === id);
            delete contactData.content[index];
            contactData.total -= 1;
          })
        );
        queryFulfilled.catch(() => {
          update.undo();
        });
      },
      invalidatesTags: (result, error, { id, customerId }) => [
        { type: 'Customers-Contacts', id },
        { type: 'Customers-Contacts', id: 'CUSTOMER_CONTACTS_LIST' },
        { type: 'Customers', id: customerId }
      ]
    })
  }),
  overrideExisting: false
});

export const {
  useGetCustomersQuery,
  useSearchCustomersQuery,
  useGetCustomerByIdQuery,
  useCreateCustomerMutation,
  useUpdateCustomerMutation,
  useDeleteCustomerMutation,
  useGetContactsQuery: useGetCustomerContactsQuery,
  useGetContactByIdQuery: useGetCustomerContactByIdQuery,
  useCreateContactMutation: useCreateCustomerContactMutation,
  useUpdateContactMutation: useUpdateCustomerContactMutation,
  useDeleteContactMutation: useDeleteCustomerContactMutation
} = customerApi;
